import './DevPanel_SchedulingTab.scss';

import React, { useMemo, useState } from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CloudIcon from '@mui/icons-material/Cloud';
import { Box, Divider, Link, Paper, Stack, TextField, Typography } from '@mui/material';

import { formatDate } from '../../utils/amplienceUtils/amplienceUtils';

/**
 * Renders a single scheduling item (Edition/Slot info)
 */
const SchedulingItem = ( { slot } ) => {
  const editionName = slot.edition?.name || slot.editionName || 'Unnamed Edition';
  const editionUrl = `${slot.edition?.url}/`;
  const contentUrl = slot.contentUrl;
  const startDate = slot.edition?.start || slot.editionStart;
  const endDate = slot.edition?.end || slot.editionEnd;
  const status = slot.edition?.status || slot.editionStatus;

  const renderStatusBadge = () => {
    if( !status ){
      return null;
    }

    const normStatus = status.toUpperCase();
    let icon = null;

    if( normStatus === 'ACTIVE' ){
      icon = <CloudIcon className='status-icon active-icon' />;
    }
    else if( normStatus === 'ENDED' ){
      icon = <CloudIcon className='status-icon ended-icon' />;
    }
    else if( normStatus === 'SCHEDULED' ){
      icon = <AccessTimeIcon className='status-icon scheduled-icon' />;
    }

    return (
      <Box className={ `status-badge status-${normStatus.toLowerCase()}` }>
        { icon }
        { status }
      </Box>
    );
  };


  return (
    <Box p={ 2 } className='scheduling-item' component={ Paper } variant='outlined'>
      <Box display='flex' justifyContent='space-between' alignItems='center' className='edition-header-row' mb={ 1 }>
        <Typography variant='subtitle1' className='edition-header'>
          { editionName }
        </Typography>
        { renderStatusBadge() }
      </Box>

      <Stack spacing={ 1 }>
        <Box className='info-section'>
          <Typography variant='caption' className='info-label' display='block'>
            Schedule Window
          </Typography>
          <Typography variant='body2' className='info-value'>
            { formatDate( startDate ) } — { formatDate( endDate ) }
          </Typography>
        </Box>

        <Divider sx={ { opacity: 0.1 } } />

        <Stack direction='row' spacing={ 2 } className='action-links'>
          <Link href={ contentUrl } target='_blank' rel='noopener' variant='body2' className='link-item' underline='hover'>
            View Content
          </Link>
          <Link href={ editionUrl } target='_blank' rel='noopener' variant='body2' className='link-item' underline='hover'>
            View Edition
          </Link>
        </Stack>

        { slot.slotName && (
          <Box className='slot-info'>
            <Typography variant='caption' className='slot-text'>
              Slot: { slot.slotName }
            </Typography>
          </Box>
        ) }
      </Stack>
    </Box>
  );
};

/**
 * Component for the Scheduling tab in DevPanel
 */
const DevPanel_SchedulingTab = ( { isLoading, error, activeScheduledData, windowWeeks } ) => {
  const [searchTerm, setSearchTerm] = useState( '' );

  const slots = activeScheduledData?.impactedBy || activeScheduledData?.slots || [];

  const filteredSlots = useMemo( () => {
    if( !searchTerm ){
      return slots;
    }
    const term = searchTerm.toLowerCase();
    return slots.filter( ( slot ) => {
      const editionName = ( slot.edition?.name || slot.editionName || '' ).toLowerCase();
      const slotName = ( slot.slotName || '' ).toLowerCase();
      return editionName.includes( term ) || slotName.includes( term );
    } );
  }, [slots, searchTerm] );

  if( isLoading ){
    return <Typography variant='body2' p={ 2 } sx={ { color: '#fff' } }>Loading scheduling info...</Typography>;
  }

  if( error ){
    return <Typography variant='body2' color='error' p={ 2 }>{ error }</Typography>;
  }

  const WeeksBanner = () => windowWeeks && (
    <Box className='window-weeks-banner'>
      <Typography variant='body2' className='weeks-text'>
        📅 Viewing scheduled content for the next <strong>{ windowWeeks }</strong> weeks.
      </Typography>
    </Box>
  );

  if( slots.length === 0 ){
    return (
      <Box p={ 2 } className='DevPanel_SchedulingTab'>
        <WeeksBanner />
        <Typography variant='body2' className='no-scheduling-info'>
          No scheduling information found for this module.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={ 2 } className='DevPanel_SchedulingTab'>
      <WeeksBanner />

      <Box className='search-box'>
        <TextField
          fullWidth
          size='small'
          placeholder='Search editions or slots...'
          value={ searchTerm }
          onChange={ ( e ) => setSearchTerm( e.target.value ) }
          variant='outlined'
        />
      </Box>

      { filteredSlots.length > 0 ? (
        filteredSlots.map( ( slot, i ) => (
          <SchedulingItem key={ slot.slotId || i } slot={ slot } />
        ) )
      ) : (
        <Typography variant='body2' sx={ { color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center', mt: 4 } }>
          No scheduling results match your search.
        </Typography>
      ) }
    </Box>
  );
};

export default DevPanel_SchedulingTab;
